"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-4 w-16 rounded-md bg-muted/60 animate-pulse" />
        <span className="text-muted-foreground/60">/</span>
        <div className="h-4 w-24 rounded-md bg-muted/60 animate-pulse" />
      </div>
    );
  }

  if (error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-sm font-default">
        {links.map((link, i) => (
          <div
            key={link.href}
            data-index={i}
            className="inline-flex items-center gap-1.5 sm:gap-2.5"
          >
            <BreadcrumbItem>
              {i === lastIndex ? (
                <BreadcrumbPage className="text-foreground font-medium">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink
                  href={link.href}
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.title}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {i < lastIndex && (
              <BreadcrumbSeparator className="text-muted-foreground/60" />
            )}
          </div>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleConsumer() {
  const { terms, loading, error } = useEcommerceTerms();

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-3 w-24 rounded-md bg-muted/60 animate-pulse" />
        <div className="h-12 w-2/3 rounded-md bg-muted/60 animate-pulse" />
        <div className="h-4 w-1/2 rounded-md bg-muted/60 animate-pulse" />
      </div>
    );
  }

  if (error || terms.length === 0) {
    return (
      <div className="space-y-3">
        <p className="font-mono text-base text-primary tracking-wide">
          {"— Our Kittens"}
        </p>
        <h1 className="font-serif font-default text-4xl sm:text-5xl lg:text-6xl font-medium text-foreground leading-tight tracking-tight">
          {"Exotic Collection"}
        </h1>
      </div>
    );
  }

  const term = terms[0];
  const isCategory = term.taxonomy === "product_cat";
  const eyebrow = isCategory ? "— Category" : "— Tag";

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
      <div className="flex-1 space-y-4">
        <p className="font-mono text-lg sm:text-xl text-primary tracking-wide">
          {eyebrow}
        </p>
        <h1 className="font-serif font-default text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium text-foreground leading-[1.05] tracking-tight">
          {term.name}
        </h1>
        {term.description && (
          <p
            className="max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed font-default"
            dangerouslySetInnerHTML={{ __html: term.description }}
          />
        )}
      </div>

      {isCategory && term.image_url && (
        <div className="relative w-full max-w-xs lg:max-w-sm shrink-0">
          <div className="absolute -inset-2 rounded-lg bg-secondary/20 -rotate-2" />
          <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-border bg-card shadow-lg">
            <img
              src={term.image_url}
              alt={term.name}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Archive() {
  const current_wp_term_query_params =
    (window as any).wvcClient.getCurrentWPTermQueryParams();
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section data-section-id="11774"
      id="archive-title-section"
      className="relative w-full bg-background text-foreground overflow-hidden"
    >
      {}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
      >
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-lg bg-secondary/20 blur-3xl" />
        <div className="absolute bottom-0 -left-16 h-64 w-64 rounded-lg bg-muted/60 blur-2xl" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        {}
        <div className="mb-8 sm:mb-10">
          <ShopBreadcrumbsProvider>
            <ShopBreadcrumbsConsumer />
          </ShopBreadcrumbsProvider>
        </div>

        {}
        <div className="mb-8 flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
          <span className="font-mono text-sm text-muted-foreground tracking-widest uppercase">
            {"Catsy Cats Archive"}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {}
        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <ArchiveTitleConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>
  );
}